import { ref, computed, onBeforeMount, onUnmounted } from 'vue'
import type { Ref } from 'vue'
import { loadDefaultJapaneseParser } from 'budoux'

const budouxParser = loadDefaultJapaneseParser()

const SP_MAX_CHARS_DEFAULT = 23
const PC_MAX_CHARS_DEFAULT = 34

interface UseRtTextFormatOptions {
  /** 整形対象のテキスト */
  text: Ref<string>
  /** ダイアログ情報（文字数設定の上書きに使用） */
  dialogInfo: Ref<Record<string, any>>
  /** dialogInfo から SP 折り返し文字数を取得するキー（省略時: 'sp_max_chars'） */
  spMaxCharsKey?: string
  /** dialogInfo から PC 折り返し文字数を取得するキー（省略時: 'pc_max_chars'） */
  pcMaxCharsKey?: string
  /** true: SP のみ折り返しを挿入し、PC は [br] を除去するだけ */
  spOnly?: boolean
}

/**
 * 1テキストに対して BudouX + [br] 折り返しを適用する composable。
 * text_1 / text_2 それぞれに独立して呼び出して使う。
 *
 * @example
 * // text_1 用（PC でも折り返しあり）
 * const { isSp, format: fmtLead, formattedText: formattedText1 } = useRtTextFormat({
 *   text: text1, dialogInfo,
 *   spMaxCharsKey: 'text_1_sp_max_chars',
 *   pcMaxCharsKey: 'text_1_pc_max_chars',
 * })
 *
 * // text_2 用（SP のみ折り返し）
 * const { formattedText: formattedText2 } = useRtTextFormat({
 *   text: text2, dialogInfo,
 *   spMaxCharsKey: 'text_2_sp_max_chars',
 *   pcMaxCharsKey: 'text_2_pc_max_chars',
 *   spOnly: true,
 * })
 *
 * // bottom_annotation_text 用（text_2 と同様）
 * const { formattedText: formattedBottomAnnotation } = useRtTextFormat({
 *   text: bottomAnnotationText, dialogInfo,
 *   spMaxCharsKey: 'bottom_annotation_sp_max_chars',
 *   pcMaxCharsKey: 'bottom_annotation_pc_max_chars',
 *   spOnly: true,
 * })
 */
export function useRtTextFormat({
  text,
  dialogInfo,
  spMaxCharsKey = 'sp_max_chars',
  pcMaxCharsKey = 'pc_max_chars',
  spOnly = false
}: UseRtTextFormatOptions) {
  const windowWidth = ref(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  )
  const isSp = computed(() => windowWidth.value < 768)

  function handleWindowResize() {
    windowWidth.value = window.innerWidth
  }

  onBeforeMount(() => window.addEventListener('resize', handleWindowResize))
  onUnmounted(() => window.removeEventListener('resize', handleWindowResize))

  /**
   * BudouX の分かち書き単位で maxChars を超えた箇所に <br> を挿入し、
   * [br] を強制改行に変換する。
   */
  function budouxFormat(str: string, maxChars: number): string {
    return str
      .split('[br]')
      .map((segment) => {
        const chunks = budouxParser.parse(segment)
        const lines: string[] = []
        let current = ''
        for (const chunk of chunks) {
          if (current.length > 0 && current.length + chunk.length > maxChars) {
            lines.push(current)
            current = chunk
          } else {
            current += chunk
          }
        }
        if (current) lines.push(current)
        return lines.join('<br>')
      })
      .join('<br>')
  }

  /**
   * dialogInfo の設定値（spMaxCharsKey / pcMaxCharsKey）を参照して
   * 任意の文字列を BudouX 整形する。
   * computed の中で呼ぶことで windowWidth・dialogInfo の変化に追従する。
   */
  function format(str: string): string {
    const spMax = dialogInfo.value?.[spMaxCharsKey] ?? SP_MAX_CHARS_DEFAULT
    const pcMax = dialogInfo.value?.[pcMaxCharsKey] ?? PC_MAX_CHARS_DEFAULT
    return budouxFormat(str, isSp.value ? spMax : pcMax)
  }

  /**
   * text の整形済み HTML 文字列。
   * spOnly=true の場合:
   *   - PC + pcMaxCharsKey が dialogInfo に設定されている → BudouX 折り返しあり
   *   - PC + pcMaxCharsKey が未設定               → [br] のみ <br> に変換
   *   - SP                                        → BudouX 折り返しあり
   */
  const formattedText = computed(() => {
    if (spOnly && !isSp.value) {
      const hasPcMax = dialogInfo.value?.[pcMaxCharsKey] != null
      if (!hasPcMax) {
        // PC かつ文字数指定なし: BudouX 自動折り返しは行わず [br] のみ <br> に変換
        return text.value.replace(/\[br\]/g, '<br>')
      }
    }
    return format(text.value)
  })

  return {
    /** ウィンドウが SP サイズ（< 768px）か */
    isSp,
    /**
     * dialogInfo の設定値を使った BudouX 整形関数。
     * leadFullText などの複合テキストを構築するときに使う。
     */
    format,
    /** text を整形した computed */
    formattedText
  }
}
